import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, Input } from '@angular/core';
import { PanelComponent } from '../core/panels/panel.component';
import { QaSectionComparison } from './qa-parameter-comparator';

@Component({
  selector: 'app-comparison-summary',
  imports: [CommonModule, PanelComponent],
  template: `
    <app-panel header="Parameter Comparison Summary">
      <div class="tw-p-4">
        <!-- Overall Summary -->
        <div class="tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded-lg tw-border">
          <div class="tw-flex tw-gap-6 tw-text-sm">
            <div class="tw-flex tw-items-center tw-gap-2">
              <i class="fa-solid fa-check tw-text-green-600"></i>
              <span class="tw-font-semibold">{{ getTotalMatches() }}</span>
              <span class="tw-text-gray-600">Matches</span>
            </div>
            <div class="tw-flex tw-items-center tw-gap-2">
              <i class="fa-solid fa-xmark tw-text-red-600"></i>
              <span class="tw-font-semibold">{{ getTotalMismatches() }}</span>
              <span class="tw-text-gray-600">Mismatches</span>
            </div>
            <div class="tw-flex tw-items-center tw-gap-2">
              <i
                class="fa-solid fa-triangle-exclamation tw-text-yellow-600"
              ></i>
              <span class="tw-font-semibold">{{ getTotalMissing() }}</span>
              <span class="tw-text-gray-600">Missing</span>
            </div>
          </div>
        </div>

        <!-- Sections -->
        @for (section of sectionComparisons; track section.sectionName) {
          <div class="tw-mb-4 tw-border tw-rounded-lg tw-overflow-hidden">
            <!-- Section Header -->
            <div
              class="tw-px-4 tw-py-3 tw-bg-gray-100 tw-cursor-pointer tw-flex tw-justify-between tw-items-center hover:tw-bg-gray-200 tw-transition-colors"
              (click)="toggleSection(section)"
              role="button"
              tabindex="0"
              (keydown.enter)="toggleSection(section)"
              (keydown.space)="toggleSection(section); $event.preventDefault()"
            >
              <div class="tw-flex tw-items-center tw-gap-3">
                <span
                  class="tw-inline-flex tw-w-8 tw-h-8 tw-items-center tw-justify-center tw-rounded-full tw-border tw-text-xl tw-text-gray-800 tw-bg-white"
                  aria-hidden="true"
                >
                  {{ section.expanded ? '-' : '+' }}
                </span>
                <span class="tw-font-semibold tw-text-gray-800">{{
                  section.sectionName
                }}</span>
                <span class="tw-text-sm tw-text-gray-600"
                  >({{ section.matchCount }} matches,
                  {{ section.mismatchCount }} mismatches,
                  {{ section.missingCount }} missing)</span
                >
              </div>

              <!-- Status Indicators -->
              <div class="tw-flex tw-gap-2">
                @if (section.matchCount > 0) {
                  <span
                    class="tw-px-2 tw-py-1 tw-bg-green-100 tw-text-green-700 tw-rounded tw-text-xs tw-font-medium"
                  >
                    ✓ {{ section.matchCount }}
                  </span>
                }
                @if (section.mismatchCount > 0) {
                  <span
                    class="tw-px-2 tw-py-1 tw-bg-red-100 tw-text-red-700 tw-rounded tw-text-xs tw-font-medium"
                  >
                    ✗ {{ section.mismatchCount }}
                  </span>
                }
                @if (section.missingCount > 0) {
                  <span
                    class="tw-px-2 tw-py-1 tw-bg-yellow-100 tw-text-yellow-700 tw-rounded tw-text-xs tw-font-medium"
                  >
                    ⚠ {{ section.missingCount }}
                  </span>
                }
              </div>
            </div>

            <!-- Section Content -->
            @if (section.expanded) {
              <div class="tw-bg-white">
                <table class="tw-w-full tw-text-sm">
                  <thead>
                    <tr class="tw-bg-gray-50 tw-border-b">
                      <th
                        class="tw-px-4 tw-py-3 tw-text-left tw-font-semibold tw-text-gray-700"
                      >
                        Parameter
                      </th>
                      <th
                        class="tw-px-4 tw-py-3 tw-text-left tw-font-semibold tw-text-gray-700"
                      >
                        {{ getReportLabel(report1Type) }}
                      </th>
                      <th
                        class="tw-px-4 tw-py-3 tw-text-left tw-font-semibold tw-text-gray-700"
                      >
                        {{ getReportLabel(report2Type) }}
                      </th>
                      <th
                        class="tw-px-4 tw-py-3 tw-text-center tw-font-semibold tw-text-gray-700 tw-w-20"
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (
                      result of section.results;
                      track result.path + result.value1 + result.value2
                    ) {
                      <tr
                        class="tw-border-b hover:tw-bg-gray-50 tw-transition-colors"
                        [class.tw-bg-green-50]="result.status === 'match'"
                        [class.tw-bg-red-50]="result.status === 'mismatch'"
                        [class.tw-bg-yellow-50]="
                          result.status === 'missing-left' ||
                          result.status === 'missing-right'
                        "
                      >
                        <td
                          class="tw-px-4 tw-py-3 tw-font-mono tw-text-xs tw-text-gray-700"
                        >
                          <span class="tw-font-medium">{{
                            result.parameter
                          }}</span>
                        </td>
                        <td
                          class="tw-px-4 tw-py-3 tw-font-mono tw-text-xs tw-text-gray-800"
                          [class.tw-text-gray-400]="result.value1 === 'N/A'"
                        >
                          {{ result.value1 }}
                        </td>
                        <td
                          class="tw-px-4 tw-py-3 tw-font-mono tw-text-xs tw-text-gray-800"
                          [class.tw-text-gray-400]="result.value2 === 'N/A'"
                        >
                          {{ result.value2 }}
                        </td>
                        <td class="tw-px-4 tw-py-3 tw-text-center">
                          @if (result.status === 'match') {
                            <i
                              class="fa-solid fa-check tw-text-green-600 tw-text-lg"
                              aria-label="Match"
                            ></i>
                          } @else if (result.status === 'mismatch') {
                            <i
                              class="fa-solid fa-xmark tw-text-red-600 tw-text-lg"
                              aria-label="Mismatch"
                            ></i>
                          } @else if (result.status === 'missing-left') {
                            <i
                              class="fa-solid fa-arrow-left tw-text-yellow-600"
                              aria-label="Missing in Report 1"
                              title="Only in Report 2"
                            ></i>
                          } @else if (result.status === 'missing-right') {
                            <i
                              class="fa-solid fa-arrow-right tw-text-yellow-600"
                              aria-label="Missing in Report 2"
                              title="Only in Report 1"
                            ></i>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        }
      </div>
    </app-panel>
  `,
  styles: ``,
})
export class ComparisonSummaryComponent {
  private cdr = inject(ChangeDetectorRef);

  @Input() sectionComparisons: QaSectionComparison[] = [];
  @Input() report1Type = '';
  @Input() report2Type = '';

  getReportLabel(reportType: string): string {
    return reportType?.toUpperCase() === 'JSON' ? 'VIAONE5' : 'CLASSIC';
  }

  toggleSection(section: QaSectionComparison): void {
    section.expanded = !section.expanded;
    this.cdr.markForCheck();
  }

  getTotalMatches(): number {
    return this.sectionComparisons.reduce(
      (sum, section) => sum + section.matchCount,
      0,
    );
  }

  getTotalMismatches(): number {
    return this.sectionComparisons.reduce(
      (sum, section) => sum + section.mismatchCount,
      0,
    );
  }

  getTotalMissing(): number {
    return this.sectionComparisons.reduce(
      (sum, section) => sum + section.missingCount,
      0,
    );
  }
}
